/**
 * Persists the most recent AI conversation id per company so a page reload
 * resumes the existing chat instead of creating a new one.
 *
 * Storage key: `focused-view:lead-conversations` → `Record<companyId, { id, savedAt }>`.
 *
 * The cache is best-effort: a stale id (e.g. server-side deleted, expired,
 * cross-account) makes the resume path 404 / 403 from the chat-message GET,
 * and the caller should fall through to `createNew`. We don't bound entries
 * for v1 — sales reps work a small set of leads at a time and the entries
 * are tiny.
 */

const STORAGE_KEY = 'focused-view:lead-conversations';

type CacheEntry = {
	id: string;
	savedAt: number;
};

type CacheShape = Record<string, CacheEntry>;

function read(): CacheShape {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return typeof parsed === 'object' && parsed !== null ? (parsed as CacheShape) : {};
	} catch {
		return {};
	}
}

function write(value: CacheShape): void {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
	} catch {
		// localStorage unavailable / quota exceeded — non-fatal.
	}
}

export function getCachedConversationId(companyId: string | undefined): string | undefined {
	if (!companyId) return undefined;
	return read()[companyId]?.id;
}

export function setCachedConversationId(companyId: string | undefined, conversationId: string): void {
	if (!companyId) return;
	const cache = read();
	cache[companyId] = { id: conversationId, savedAt: Date.now() };
	write(cache);
}

export function clearCachedConversationId(companyId: string | undefined): void {
	if (!companyId) return;
	const cache = read();
	if (!(companyId in cache)) return;
	delete cache[companyId];
	write(cache);
}
